import React, { useCallback, useEffect, useState } from 'react'

import { v4 as uniqid } from 'uuid'

import SubmitForm from '@/Form/V2/SubmitForm'
import Checkbox   from '@/Form/V2/PullState/Checkbox'
import Input      from '@/Form/V2/Input'
import Radio      from '@/Form/V2/Radio'

import getFormStatus from '@/Form/Functions/getFormStatus'

import request from '@function/ajax/request'

import Loader from '@Pure/Loader'

import { isTrue } from '@root/utility'

import FormsStore from '@Store/Forms'

const FORM_NAME = 'MyAccountForm';

const { app } = window;

function getFormInit (user)
{
	const data_from = user ? user : app.state.session;

	const form =
	{
		login: data_from.login,
		password: '',

		firstname: data_from.firstname,
		lastname: data_from.lastname,
		phone: app.getSpacedPhoneNumber(data_from.phone),

		'address[zip_code]': data_from.address?.zip_code || '',
		'address[city]': data_from.address?.city || '',
		'address[line_1]': data_from.address?.line_1 || '',
		'address[line_2]': data_from.address?.line_2 || '',

		is_visible_by_members: isTrue(data_from.is_visible_by_members)
	};

	return form;
}

export default function MyAccountForm ({ ...props })
{
	const [submit, setSubmit] = useState();
	const [submitting, setSubmitting] = useState(false);
	const [status, setStatus] = useState({ });
	const [my_role, setMyRole] = useState(app.state.session?.permissions?.title);

	const has_login = FormsStore.useState(s => s[FORM_NAME]?.login ? true : false);

	const newsletter = FormsStore.useState(function (store)
	{
		return isTrue(store[FORM_NAME]?.newsletter)
		? true
		: [false, 'false'].includes(store[FORM_NAME]?.newsletter)
		? false
		: undefined;
	});

	useEffect(function ()
	{
		const controller = new AbortController();

		(async function ()
		{
			try
			{
				var { consent } = await request('/users/get-my-consent')
				.config({ controller })
				.post({ });
			}
			catch (update)
			{
				if (controller?.signal?.aborted) return;

				return setStatus(update);
			}

			if (isTrue(consent.newsletter) ||
				[false, 'false'].includes(consent.newsletter))
			{
				FormsStore.update(function (store)
				{
					store[FORM_NAME] ||= { };

					store[FORM_NAME].newsletter = isTrue(consent.newsletter);
				});
			}
		})();

		return () => controller?.abort?.();
	},
	[]);

	useEffect(function ()
	{
		app.push(
		{
			event: 'sessionChange',
			cb: setData
		});

		if (app.state.session?.login)
		{
			setData();
		}

		return () => { app.delete({ event: 'sessionChange', cb: setData }) }

		function setData ()
		{
			setMyRole(app.state.session?.permissions?.title);

			if (app.state.session?.login)
			{
				FormsStore.update(function (store)
				{
					store[FORM_NAME] ||= { };

					const newsletter = store[FORM_NAME].newsletter;

					store[FORM_NAME] = getFormInit();
					store[FORM_NAME].newsletter = newsletter;
				});
			}
		}
	},
	[]);

	const sendForm = useCallback(function (e)
	{
		if (submitting) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME]._submit_id = uniqid();
		});

		setSubmitting(true);
		setSubmit(uniqid());
	},
	[submitting]);

	const onError = useCallback(function (update)
	{
		setSubmit(undefined);
		setStatus(update);
		setSubmitting(false);
	},
	[]);

	const onSuccess = useCallback(function (response)
	{
		setSubmit(undefined);
		setStatus({ info: response.info });
		setSubmitting(false);

		FormsStore.update(function (store)
		{
			store[FORM_NAME] ||= { };

			const newsletter = store[FORM_NAME].newsletter;

			store[FORM_NAME] = getFormInit(response.user);
			store[FORM_NAME].newsletter = newsletter;
		});

		Object.assign(app.state.session, response.user);
	},
	[]);

	const setNewsletter = useCallback(function (e)
	{
		const value = e?.target?.value;

		if (!value) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME] ||= { };

			store[FORM_NAME].newsletter = value === 'newsletter_yes';
		});

		setStatus({ });
	},
	[]);

	if (my_role === 'EC')
	{
		return getFormStatus(
		{
			error: 'Cette page n\'est pas accessible en tant ' +
				'qu\'administrateur.'
		});
	}

	const fields = has_login ?
	(
		<div className="c-form__content-fields">

			<fieldset className="o-form__fieldset">

				<legend className="o-form__fieldset-legend">
					Adresse de facturation
				</legend>

				<div className="o-form__fieldset-content">

					<Input name="address[line_1]" label="Adresse"
						form={ FORM_NAME }
						bemit={ ['50%'] }
						error={ status.fields?.['address[line_1]']?.error }
					/>

					<Input name="address[line_2]" label="Complément d'adresse"
						form={ FORM_NAME }
						bemit={ ['50%'] }
						error={ status.fields?.['address[line_2]']?.error }
					/>

					<Input name="address[zip_code]" label="Code postal"
						form={ FORM_NAME }
						bemit={ ['50%'] }
						error={ status.fields?.['address[zip_code]']?.error }
					/>

					<Input name="address[city]" label="Ville"
						form={ FORM_NAME }
						bemit={ ['50%'] }
						error={ status.fields?.['address[city]']?.error }
					/>

				</div>

			</fieldset>

			<Input name="firstname" label="Prénom"
				form={ FORM_NAME }
				bemit={ ['50%'] }
				error={ status.fields?.['firstname']?.error }
				read-only={ my_role === 'Administrateur de CE' }
			/>

			<Input name="lastname" label="Nom"
				form={ FORM_NAME }
				bemit={ ['50%'] }
				error={ status.fields?.['lastname']?.error }
				read-only={ my_role === 'Administrateur de CE' }
			/>

			<Input name="login" label="E-mail"
				type="email"
				form={ FORM_NAME }
				bemit={ ['50%'] }
				error={ status.fields?.['login']?.error }
				read-only
			/>

			<Input name="password" label="Mot de passe"
				type="password"
				placeholder="(inchangé si vide)"
				form={ FORM_NAME }
				bemit={ ['50%'] }
				error={ status.fields?.['password']?.error }
			/>

			<Input name="phone" label="Téléphone"
				form={ FORM_NAME }
				bemit={ ['50%'] }
				error={ status.fields?.['phone']?.error }
			/>

			{
				my_role === 'Administrateur de CE' ?
				(
					<Checkbox label="Être visible sur la page « Infos pratiques »"
						name="is_visible_by_members"
						bemit={ ['50%'] }
						form={ FORM_NAME }
					/>
				)
				: undefined
			}

			<div className="c-form__content-fields u-full-width">

				<div className="u-full-width u-flex u-flex-wrap u-items-center"
					style={ { gap: 30 } }>

					<span>
						Recevoir la newsletter Synergies&nbsp;?
					</span>

					<Radio label="Non"
						name="newsletter"
						value="newsletter_no"
						checked={ newsletter === false }
						onChange={ setNewsletter }
					/>

					<Radio label="Oui"
						name="newsletter"
						value="newsletter_yes"
						checked={ newsletter === true }
						onChange={ setNewsletter }
					/>

				</div>

			</div>

		</div>
	)
	:
	(
		<Loader bemit={ ['bg-white', 'primary'] }
			className="c-loader"
		/>
	);

	return (
		<>

			<form method="POST"
				action="/users/update-my-account"
				className="c-form c-form--my-account">

				<h2 className="c-title c-title--form">
					Informations
				</h2>

				{ fields }

			</form>

			{
				submit ?
				(
					<SubmitForm form={ FORM_NAME } id={ submit }
						onError={ onError }
						onSuccess={ onSuccess }
						url="/users/update-my-account"
					/>
				)
				: undefined
			}

			{ getFormStatus(status, { style: { marginBottom: 0 } }) }

			<button type="button" className="c-btn c-btn--extra c-btn--green
				u-margin-top-30"
				onClick={ sendForm }>
				Mettre à jour
				{
					submitting ?
					(
						<Loader bemit={ ['bg-green', 'white'] }
							className="c-loader"
						/>
					)
					: undefined
				}
			</button>

		</>
	);
}